/**
 * API Key Service
 *
 * Provides CRUD operations for developer API keys — tokens that allow
 * programmatic access to the GBrands platform. Only users with the
 * 'developer' role can create and manage API keys.
 *
 * The full plain-text key is only returned ONCE at creation time (or
 * regeneration). After that, only the key prefix (e.g., "gb_dk_a3...")
 * is stored and returned by the API. Clients must copy the key at
 * creation time; it cannot be retrieved later.
 *
 * Backend endpoints (all require developer role):
 *	 GET	/api/v1/api-keys			  — List all keys for the authenticated user
 *	 POST	/api/v1/api-keys			  — Generate a new API key
 *	 DELETE /api/v1/api-keys/:id		  — Revoke (deactivate) an API key
 *	 POST	/api/v1/api-keys/:id/regenerate — Regenerate an existing key
 *
 * All endpoints require authentication via Sanctum Bearer token and
 * the DeveloperOnly middleware on the backend.
 */

import { api } from './api';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/**
 * Represents an API key as returned by the list endpoint.
 *
 * @property id - Unique identifier for the key record
 * @property name - Human-readable label (e.g., "Production Integration")
 * @property prefix - First few characters of the key for identification (e.g., "gb_dk_a3...")
 * @property is_active - Whether the key is currently active and can authenticate requests
 * @property expires_at - ISO timestamp of when the key expires (null if no expiration)
 * @property last_used_at - ISO timestamp of the last time the key was used (null if never used)
 * @property created_at - ISO timestamp of when the key was created
 */
export interface ApiKey {
	id: string | number;
	name: string;
	prefix: string;
	is_active: boolean;
	expires_at: string | null;
	last_used_at: string | null;
	created_at: string;
}

/**
 * Response shape when a new API key is created or regenerated.
 * Includes the full plain-text key which is only available at this moment.
 *
 * @property key - The API key record metadata
 * @property plain_text_key - The full key string (only shown once; must be copied immediately)
 */
export interface ApiKeyCreateResponse {
	key: ApiKey;
	plain_text_key: string;
}

/* ============================================
 * SERVICE METHODS
 * ============================================ */

/**
 * API Key service methods.
 * Each method maps to a specific backend API endpoint.
 */
export const apiKeyService = {
	/**
	 * GET /api-keys — List all API keys for the authenticated developer.
	 * Returns key metadata only (prefix, not the full key).
	 */
	getAll: () =>
		api.get<ApiKey[]>('/api-keys'),

	/**
	 * POST /api-keys — Generate a new API key.
	 * Returns the full plain-text key in the response (only time it's visible).
	 *
	 * @param name - Human-readable label for the key (e.g., "Production Integration")
	 * @param expiresAt - Optional ISO date string for key expiration
	 */
	create: (name: string, expiresAt?: string) =>
		api.post<ApiKeyCreateResponse>('/api-keys', {
			name,
			...(expiresAt ? { expires_at: expiresAt } : {}),
		}),

	/**
	 * DELETE /api-keys/:id — Revoke (deactivate) an API key.
	 * Sets is_active to false on the backend; the key can no longer authenticate.
	 *
	 * @param id - The ID of the API key to revoke
	 */
	revoke: (id: string | number) =>
		api.delete<void>(`/api-keys/${id}`),

	/**
	 * POST /api-keys/:id/regenerate — Regenerate an existing API key.
	 * Deactivates the old key and creates a new one with the same name.
	 * Returns the new full plain-text key (only time it's visible).
	 *
	 * @param id - The ID of the API key to regenerate
	 */
	regenerate: (id: string | number) =>
		api.post<ApiKeyCreateResponse>(`/api-keys/${id}/regenerate`),
};
